import { FC, useState, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'

import MaxWidthLimiter from 'containers/max-width-limiter/max-width-limiter'
import HtmlContainer from 'containers/html-container/html-container'
import Recommendations from 'containers/recommendations/recommendations'
import ScrollEventWrap from 'containers/scroll-event-wrap/scroll-event-wrap'
import ArticleTitle from '../parts/article-title/article-title'
import AttachedItems from '../../parts/attached-items/attached-items'
import Commentaries from '../../parts/commentaries/commentaries'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
const TableOfContents = dynamic(() => import('../../parts/table-of-contents/table-of-contents'), {
  ssr: false,
})
import ArticleDateViews from '../../parts/article-date-views/article-date-views'
import LikeShare from '../../parts/like-share/like-share'
import useProgressBar from 'hooks/useProgressBar'

import { HtmlContentStatus } from 'constants/article-data'
import { originSubscribe, originFWebinar } from 'utils/analytics/webinars/webinar_future'
import { A_CLINIC_SCROLL } from 'constants/analytic-data'
import { URL_SITE } from 'constants/data'
import { IArticle, ITable } from './article-clinic.type'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import { formatHtml } from './utils'
import { A_cr_openClinRec, originClinRec } from 'utils/analytics/library/clinrec'
import { useARegAndLogin, originReg } from 'utils/analytics/other/registration/registration'

import styles from './../articles.module.scss'

interface IPageContent {
  tableOfContents: ITable[]
  htmlContentWithIds: string
}

const ArticleClinic: FC<IArticle> = ({
  id,
  name,
  startDate,
  Themes,
  htmlContent,
  filesList,
  Comments,
  isHideComments,
  setSEODescription,
}: IArticle) => {
  const [pageContentWithIds, setPageContentWithIds] = useState({
    htmlContentWithIds: htmlContent,
    tableOfContents: [],
  } as IPageContent)
  const articleContentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [commentsAmount, setCommentsAmount] = useState(0)

  useEffect(() => {
    const [content, headers] = formatHtml(htmlContent, id)

    setPageContentWithIds({ htmlContentWithIds: content, tableOfContents: headers })
    A_cr_openClinRec(originClinRec.NOORIGIN, id)
  }, []) //статья с якорями + список всех заголовков из статьи (содержание)

  useEffect(() => {
    const MKB = document.querySelector('#mkb')
    if (!MKB) return

    const desc = `${name}, код МКБ 10: ${MKB.textContent}, симптомы, диагностика, лечение, профилактика, рекомендации для врачей на сайте Medpoint`
    setSEODescription(desc)
  }, [])

  useARegAndLogin(originReg.CLINICALCASE)
  useProgressBar()

  return (
    <ScrollEventWrap containerRef={containerRef} event={A_CLINIC_SCROLL}>
      <div className={styles.article}>
        <div className={styles.articleWrapper}>
          <div className={styles.breadcrumb}>
            <BreadcrumbsPage
              breadData={[
                { name: 'Библиотека', link: `${URL_SITE}/library` },
                { name, link: '' },
              ]}
            ></BreadcrumbsPage>
          </div>
          <ArticleTitle
            themes={Themes}
            title={name}
            startDate={startDate}
            comments={commentsAmount}
          />

          <div className={styles.articleTop}>
            <MaxWidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
              <div className={styles.whiteBg}>
                <div className={styles.articleWrapper} ref={articleContentRef}>
                  <main className={styles.content} ref={containerRef}>
                    <HtmlContainer
                      htmlContent={pageContentWithIds.htmlContentWithIds}
                      contentStatus={HtmlContentStatus.WITH_HORIZ_SCROLL}
                      noDivide={true}
                    />
                  </main>
                  <div className={styles.bottom}>
                    <div className={styles.likeBottomLine}>
                      <LikeShare AData={{ origin: originShare.CLINREC }} />
                      <div className={styles.views}>
                        <ArticleDateViews comments={commentsAmount} />
                      </div>
                    </div>
                  </div>
                  {filesList && Boolean(filesList.length) && (
                    <div className={styles.attached}>
                      <AttachedItems items={filesList} />
                    </div>
                  )}
                </div>
              </div>
            </MaxWidthLimiter>
            <TableOfContents
              tableOfContents={pageContentWithIds.tableOfContents}
              articleContentRef={articleContentRef}
            />
          </div>
          <MaxWidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
            <Recommendations
              noPadding
              noFixedWidth
              blockModificator={styles.blockModificator}
              cardModificator={styles.cardModificator}
              AData={{
                originSub: originSubscribe.NOORIGIN,
                originClick: originFWebinar.NOORIGIN,
              }}
            />
          </MaxWidthLimiter>
          {!isHideComments && (
            <div className={styles.commentaries}>
              <Commentaries
                comments={Comments}
                materialType="article"
                id={id}
                getCommentsAmount={setCommentsAmount}
              />
            </div>
          )}
        </div>
      </div>
    </ScrollEventWrap>
  )
}

export default ArticleClinic
